import React, { useEffect, useState } from "react";

import { RequireUserAuth, useUserContext, useUserDispatchContext } from "../context/User";
import { createUserPet, getUserPetByID, updateUserPetByID } from "../services/api";
import { breeds as breedList } from "../services/breeds";
import { PetAPIResponseType, PetFileType } from "../types/pet";
import Button from "./Button";
import ImageUpload from "./ImageUpload";
import SideBarContent from "./SideBarContent";
import SideBarFooter from "./SideBarFooter";
import Input from "./ui/Input";
import { MultiSelect } from "./ui/MultiSelect";
import Select from "./ui/SelectSimple";
import Textarea from "./ui/Textarea";

const breedsForSelect = breedList.reduce((acc, i) => {
  return [...acc, [i.name, i.name]];
}, []);

const defaultPet = {
  name: "",
  desc: "",
  gender: "",
  birthdate: "",
  size: "",
  breed: "",
  breedType: "",
  age: "",
  fur: "",
  mainImage: {},
  images: [],
};
const staticUrl: string = process.env.STATIC_URL;

type Errors = {
  [s: string]: boolean;
};

const UserPageDogEdit = () => {
  const [loading, setLoading] = useState(true);
  const [pet, setPet] = useState(defaultPet as PetAPIResponseType);
  const [mainImage, setMainImage] = useState({} as PetFileType);
  const [images, setImages] = useState([] as PetFileType[]);
  const [errors, setErrors] = useState({} as Errors);

  const { user } = useUserContext();
  const userDispatch = useUserDispatchContext();

  useEffect(() => {
    console.log("usr", user);
    if (user.pageParams && user.pageParams.id) {
      fetchPet(user.pageParams.id);
    } else {
      setPet(defaultPet);
      setMainImage({});
      setImages([]);
      setErrors({});
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    console.log("mainImage", mainImage);
  }, [mainImage]);

  useEffect(() => {
    console.log("images", images);
  }, [images]);

  const fetchPet = (petId: string) => {
    if (!user.accessToken) {
      return;
    }

    void getUserPetByID(user.accessToken, petId)
      .then((data) => {
        if (!data) {
          return;
        }
        const im = data.images.find((i) => i.main);
        if (im) {
          setMainImage({
            preview: `${staticUrl}${im.url}`,
            id: im.id,
          });
        }

        const imgs = data.images
          .filter((i) => !i.main)
          .reduce((acc, im) => {
            const i = {
              preview: `${staticUrl}${im.url}`,
              id: im.id,
            };
            return [...acc, i];
          }, []);
        if (imgs) {
          setImages(imgs);
        }
        console.log("PET FETCHED", data);
        setPet(data);
      })
      .catch((e) => console.log(e))
      .finally(() => setLoading(false));
  };

  const handleMainImageChange = (file: FileList) => {
    const img = {
      preview: URL.createObjectURL(file[0]),
      data: file[0],
    };
    setMainImage(img);
  };

  const handleImagesChange = (files: FileList) => {
    const images = [];
    for (let i = 0; i < files.length; i++) {
      images.push({
        preview: URL.createObjectURL(files.item(i)),
        data: files.item(i),
      });
    }

    if (images.length) {
      setImages(images);
    }
  };

  const validateFields = () => {
    const er = {};
    Object.entries(defaultPet).map((el) => {
      const [key] = el;
      if (typeof pet[key] === "string" && pet[key].length <= 1) {
        er[key] = true;
      }
      if (key === "mainImage" && Object.keys(mainImage).length === 0) {
        er[key] = true;
      }
      if (key === "images" && images.length === 0) {
        er[key] = true;
      }
    });

    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSavePet = () => {
    console.log("PET", pet);
    if (!user.accessToken) {
      return;
    }

    const status = validateFields();
    if (!status) {
      return;
    }

    setLoading(true);

    const petToSave = { ...pet };
    delete petToSave.images;
    delete petToSave.mainImage;
    petToSave.vaccines = JSON.stringify(petToSave.vaccines);
    petToSave.treatments = JSON.stringify(petToSave.treatments);
    petToSave.travelRequirements = JSON.stringify(petToSave.travelRequirements);

    console.log("PETTOSAVE", petToSave);

    const fd = new FormData();
    Object.entries(petToSave).map((entry) => {
      const [key, value] = entry;
      fd.append(key, value || "");
    });

    if (mainImage.data) {
      fd.append("mainImage", mainImage.data);
    }

    images.map((i) => {
      if (i.data) {
        fd.append("images", i.data);
      }
    });

    console.log("PETTOSAVE FD", fd);
    if (pet.id) {
      updateUserPetByID(user.accessToken, pet.id, fd)
        .then(() => {
          userDispatch({ type: "set-page", payload: { page: "our-dogs" } });
          return;
        })
        .catch((e) => console.log(e))
        .finally(() => setLoading(false));
    } else {
      createUserPet(user.accessToken, fd)
        .then(() => {
          userDispatch({ type: "set-page", payload: { page: "our-dogs" } });
          return;
        })
        .catch((e) => console.log(e))
        .finally(() => setLoading(false));
    }
  };

  const renderFooter = () => {
    return (
      <div className="flex flex-row items-center justify-end gap-4">
        <div
          onClick={() => userDispatch({ type: "set-page", payload: { page: "our-dogs" } })}
          className="cursor-pointer font-simianregular text-[18px]"
        >
          Cancel
        </div>
        <Button title="Save changes" onClick={onSavePet} theme="dark" />
      </div>
    );
  };

  const formatBoleanValue = (val: boolean) => {
    if (val === null) {
      return "";
    } else if (val) {
      return "1";
    } else {
      return "0";
    }
  };

  if (loading) {
    return;
  }

  return (
    <>
      <SideBarContent>
        <div className="mb-10 flex flex-col gap-10 text-[#474747]">
          <div className="flex flex-col gap-4">
            <div className="font-simianregular text-[20px] leading-none text-[#0B0B0B]">
              Main image
            </div>
            <div>
              Ensure your uploaded image are high-quality JPEG or PNG files, with a maximum size of
              2 MB per file.
            </div>
            <ImageUpload
              files={[mainImage]}
              onChangeHandler={handleMainImageChange}
              err={errors.mainImage}
            />
          </div>
          <div className="relative flex flex-col gap-4">
            <div className="font-simianregular text-[20px] leading-none text-[#0B0B0B]">Images</div>
            <div>
              Ensure your uploaded image are high-quality JPEG or PNG files, with a maximum size of
              2 MB per file.
            </div>
            <ImageUpload
              files={images}
              onChangeHandler={handleImagesChange}
              err={errors.images}
              multiple={true}
            />
          </div>
          <div className="flex max-w-[850px] flex-col gap-4">
            <div className="font-simianregular text-[20px] leading-none text-[#0B0B0B]">
              Dog description
            </div>
            <div>
              <Input
                name="name"
                label="Name"
                value={pet.name}
                onChange={(e) =>
                  setPet((prevState) => {
                    return { ...prevState, name: e.target.value };
                  })
                }
                error={errors.name}
              />
            </div>
            <div>
              <Textarea
                name="desc"
                label="Personality and story"
                value={pet.desc}
                rows={10}
                onChange={(e) =>
                  setPet((prevState) => {
                    return { ...prevState, desc: e.target.value };
                  })
                }
                error={errors.desc}
              />
            </div>
            <div>
              <Select
                name="gender"
                label="Sex"
                value={pet.gender || ""}
                placeholder="Select gender"
                options={[
                  ["male", "Male"],
                  ["female", "Female"],
                ]}
                onChange={(e) =>
                  setPet((prevState) => {
                    return { ...prevState, gender: e.target.value };
                  })
                }
                error={errors.gender}
              />
            </div>
            <div>
              <Select
                name="size"
                label="Size"
                value={pet.size || ""}
                placeholder="Select size"
                options={[
                  ["small", "Up to 10 kg (22 lbs)"],
                  ["medium", "10–25 kg (22–55 lbs)"],
                  ["large", "Over 25 kg (55 lbs)"],
                ]}
                onChange={(e) =>
                  setPet((prevState) => {
                    return { ...prevState, size: e.target.value };
                  })
                }
                error={errors.size}
              />
            </div>
            <div>
              <Select
                name="breedType"
                label="Breed type"
                value={pet.breedType || ""}
                placeholder="Select type"
                options={[
                  ["pure", "Pure"],
                  ["mixed", "Mixed"],
                ]}
                onChange={(e) =>
                  setPet((prevState) => {
                    return { ...prevState, breedType: e.target.value };
                  })
                }
                error={errors.breedType}
              />
            </div>
            <div>
              <Select
                name="breed"
                label="Breed"
                value={pet.breed || ""}
                placeholder="Select breed"
                options={breedsForSelect}
                onChange={(e) =>
                  setPet((prevState) => {
                    return { ...prevState, breed: e.target.value };
                  })
                }
                error={errors.breed}
              />
            </div>
            {pet.breedType === "mixed" && (
              <div>
                <Select
                  name="breedAdditional"
                  label="Breed (secondary)"
                  value={pet.breedAdditional?.length > 0 ? pet.breedAdditional[0] : ""}
                  placeholder="Select secondary breed"
                  options={breedsForSelect}
                  onChange={(e) =>
                    setPet((prevState) => {
                      return { ...prevState, breedAdditional: e.target.value };
                    })
                  }
                  error={errors.breedAdditional}
                />
              </div>
            )}
            <div>
              <Select
                name="age"
                label="Age"
                value={pet.age || ""}
                placeholder="Select age"
                options={[
                  ["puppy", "Puppy (0–1 year)"],
                  ["young", "Young Adult (1–3 years)"],
                  ["adult", "Adult (3–7 years)"],
                  ["senior", "Senior (7–10+ years)"],
                  ["geriatric", "Geriatric (10+ years)"],
                ]}
                onChange={(e) =>
                  setPet((prevState) => {
                    return { ...prevState, age: e.target.value };
                  })
                }
                error={errors.age}
              />
            </div>
            <div>
              <Input
                name="birthdate"
                label="Birthdate"
                value={pet.birthdate}
                onChange={(e) =>
                  setPet((prevState) => {
                    return { ...prevState, birthdate: e.target.value };
                  })
                }
                error={errors.birthdate}
              />
            </div>
            <div>
              <Select
                name="fur"
                label="Fur"
                value={pet.fur || ""}
                placeholder="Select fur"
                options={[
                  ["short", "Short"],
                  ["medium", "Medium"],
                  ["long", "Long"],
                  ["coarse", "Coarse"],
                  ["curly", "Curly"],
                  ["double", "Double"],
                ]}
                onChange={(e) =>
                  setPet((prevState) => {
                    return { ...prevState, fur: e.target.value };
                  })
                }
                error={errors.fur}
              />
            </div>
          </div>
          <div className="flex max-w-[850px] flex-col gap-4">
            <div className="font-simianregular text-[20px] leading-none text-[#0B0B0B]">
              Dog characterisitcs
            </div>
            <div>
              <Select
                name="housingSuitability"
                label="Housing Suitability"
                value={pet.housingSuitability || ""}
                placeholder="Select option"
                options={[
                  ["large", "Requires large to extra large housing"],
                  ["any", "Ok with any size of home"],
                ]}
                onChange={(e) =>
                  setPet((prevState) => {
                    return { ...prevState, housingSuitability: e.target.value };
                  })
                }
                error={errors.childrenSuitability}
              />
              {/* <MultiSelect
                name="housingSuitability"
                label="Housing Suitability"
                defaultValue={pet.housingSuitability || []}
                options={[
                  { label: "Requires large to extra large housing", value: "large" },
                  { label: "Ok with any size of home", value: "any" },
                ]}
                // onValueChange={(e) => console.log("VVV", e)}
                onValueChange={(e) =>
                  setPet((prevState) => {
                    return { ...prevState, housingSuitability: e };
                  })
                }
                error={errors.housingSuitability}
              /> */}
            </div>
            <div>
              <Select
                name="outdoorSituation"
                label="Outdoor situation"
                value={pet.outdoorSituation || ""}
                placeholder="Select option"
                options={[
                  ["city", "Works anywhere, apartment-friendly"],
                  [
                    "suburban",
                    "Strive in quieter neighborhoods with access to parks and green spaces.",
                  ],
                  ["rural", "Requires frequent access to forests, fields, or large outdoor areas"],
                ]}
                onChange={(e) =>
                  setPet((prevState) => {
                    return { ...prevState, outdoorSituation: e.target.value };
                  })
                }
                error={errors.outdoorSituation}
              />
            </div>
            <div>
              <Select
                name="childrenSuitability"
                label="Suitable for children below 12"
                value={pet.childrenSuitability || ""}
                placeholder="Select level"
                options={[
                  ["yes", "Yes"],
                  ["no", "No"],
                ]}
                onChange={(e) =>
                  setPet((prevState) => {
                    return { ...prevState, childrenSuitability: e.target.value };
                  })
                }
                error={errors.childrenSuitability}
              />
            </div>
            <div>
              <Select
                name="dogsSuitability"
                label="Suitable for to co-exist with other dogs"
                value={pet.dogsSuitability || ""}
                placeholder="Select option"
                options={[
                  ["yes", "Yes"],
                  ["no", "No"],
                ]}
                onChange={(e) =>
                  setPet((prevState) => {
                    return { ...prevState, dogsSuitability: e.target.value };
                  })
                }
                error={errors.dogsSuitability}
              />
            </div>
            <div>
              <Select
                name="dogsSuitability"
                label="Suitable for to co-exist with other animals"
                value={pet.animalsSuitability || ""}
                placeholder="Select option"
                options={[
                  ["yes", "Yes"],
                  ["no", "No"],
                ]}
                onChange={(e) =>
                  setPet((prevState) => {
                    return { ...prevState, animalsSuitability: e.target.value };
                  })
                }
                error={errors.animalsSuitability}
              />
            </div>
            <div>
              <Select
                name="allergySuitability"
                label="Allergy-friendly fur"
                value={pet.allergySuitability || ""}
                placeholder="Select option"
                options={[
                  ["yes", "Yes"],
                  ["no", "No"],
                ]}
                onChange={(e) =>
                  setPet((prevState) => {
                    return { ...prevState, allergySuitability: e.target.value };
                  })
                }
                error={errors.allergySuitability}
              />
            </div>
            <div>
              <Select
                name="aloneTolerance"
                label="Alone tolerance"
                value={pet.aloneTolerance || ""}
                placeholder="Select option"
                options={[
                  ["1", "High support needs / emotional"],
                  ["4", "Some alone tolerance"],
                  ["7", "High alone tolerance"],
                ]}
                onChange={(e) =>
                  setPet((prevState) => {
                    return { ...prevState, aloneTolerance: e.target.value };
                  })
                }
                error={errors.aloneTolerance}
              />
            </div>
            <div>
              <Select
                name="activityLevel"
                label="Dog Activity Level"
                value={pet.activityLevel || ""}
                placeholder="Select level"
                options={[
                  ["low", "A calm dog no requirements of high activity level"],
                  ["moderate", "Medium activity level 2-4 normal walks/day"],
                  ["high", "High demand of activity"],
                ]}
                onChange={(e) =>
                  setPet((prevState) => {
                    return { ...prevState, activityLevel: e.target.value };
                  })
                }
                error={errors.activityLevel}
              />
            </div>
            <div>
              <Select
                name="adaptability"
                label="Adaptability / Boarding need"
                value={pet.adaptability || ""}
                placeholder="Select option"
                options={[
                  ["adaptable", "High alone tolerance, longterm indipendence"],
                  ["medium", "Some alone tolerance, no problem to leave for 1-2 days"],
                  ["no", "High support needs, not comfortable with change"],
                ]}
                onChange={(e) =>
                  setPet((prevState) => {
                    return { ...prevState, adaptability: e.target.value };
                  })
                }
                error={errors.adaptability}
              />
            </div>
            <div>
              <Select
                name="experience"
                label="Experience needed"
                value={pet.experience || ""}
                placeholder="Select option"
                options={[
                  ["no", "Suitable for first-time dog owners"],
                  ["some", "Best with some dog experience"],
                  ["yes", "Requires an experienced owner"],
                ]}
                onChange={(e) =>
                  setPet((prevState) => {
                    return { ...prevState, experience: e.target.value };
                  })
                }
                error={errors.experience}
              />
            </div>
            <div>
              <Select
                name="supportLevel"
                label="Everyday support need"
                value={pet.supportLevel || ""}
                placeholder="Select option"
                options={[
                  ["flexible", "Low support needs. Emotionally resilient and adaptable"],
                  ["regular", "Moderate support needs. Benefits from routine and reassurance"],
                  ["stable", "High support needs. Needs stability, closeness, and extra support"],
                ]}
                onChange={(e) =>
                  setPet((prevState) => {
                    return { ...prevState, supportLevel: e.target.value };
                  })
                }
                error={errors.supportLevel}
              />
            </div>
            <div>
              <Select
                name="energyLevel"
                label="Indoor energy level"
                value={pet.energyLevel || ""}
                placeholder="Select option"
                options={[
                  ["calm", "Calm indoor"],
                  ["balanced", "Balanced"],
                  ["high", "High indoor activity"],
                ]}
                onChange={(e) =>
                  setPet((prevState) => {
                    return { ...prevState, energyLevel: e.target.value };
                  })
                }
                error={errors.energyLevel}
              />
            </div>
            <div>
              <Select
                name="groomingNeeds"
                label="Grooming needs"
                value={pet.groomingNeeds || ""}
                placeholder="Select option"
                options={[
                  ["1", "Low maintenance"],
                  ["2", "Medium"],
                  ["3", "High maintenance"],
                ]}
                onChange={(e) =>
                  setPet((prevState) => {
                    return { ...prevState, groomingNeeds: e.target.value };
                  })
                }
                error={errors.groomingNeeds}
              />
            </div>
            <div>
              <Select
                name="temperament"
                label="Temperament preference"
                value={pet.temperament || ""}
                placeholder="Select option"
                options={[
                  ["independent", "Independent"],
                  ["balanced", "Balanced"],
                  ["attention", "Needs lots of attention"],
                ]}
                onChange={(e) =>
                  setPet((prevState) => {
                    return { ...prevState, temperament: e.target.value };
                  })
                }
                error={errors.temperament}
              />
            </div>
          </div>
          <div className="flex max-w-[850px] flex-col gap-4">
            <div className="font-simianregular text-[20px] leading-none text-[#0B0B0B]">
              Vaccines and Treatments
            </div>
            <div>
              <Select
                name="vaccines"
                label="Rabies"
                value={pet.vaccines?.rabies || ""}
                placeholder="Select option"
                options={[
                  ["1", "Yes"],
                  ["0", "No"],
                ]}
                onChange={(e) =>
                  setPet((prevState) => {
                    return {
                      ...prevState,
                      vaccines: { ...prevState.vaccines, rabies: e.target.value },
                    };
                  })
                }
                error={errors.vaccines}
              />
            </div>
            <div>
              <Select
                name="vaccines"
                label="DHPP"
                value={pet.vaccines?.dhpp || ""}
                placeholder="Select option"
                options={[
                  ["1", "Yes"],
                  ["0", "No"],
                ]}
                onChange={(e) =>
                  setPet((prevState) => {
                    return {
                      ...prevState,
                      vaccines: { ...prevState.vaccines, dhpp: e.target.value },
                    };
                  })
                }
                error={errors.vaccines}
              />
            </div>
            <div>
              <Select
                name="treatments"
                label="Flea and Tick Treatment"
                value={pet.treatments?.flea || ""}
                placeholder="Select option"
                options={[
                  ["1", "Yes"],
                  ["0", "No"],
                ]}
                onChange={(e) =>
                  setPet((prevState) => {
                    return {
                      ...prevState,
                      treatments: { ...prevState.treatments, flea: e.target.value },
                    };
                  })
                }
                error={errors.treatments}
              />
            </div>
            <div>
              <Select
                name="treatments"
                label="Deworming"
                value={pet.treatments?.deworming || ""}
                placeholder="Select option"
                options={[
                  ["1", "Yes"],
                  ["0", "No"],
                ]}
                onChange={(e) =>
                  setPet((prevState) => {
                    return {
                      ...prevState,
                      treatments: { ...prevState.treatments, deworming: e.target.value },
                    };
                  })
                }
                error={errors.treatments}
              />
            </div>
          </div>
          <div className="flex max-w-[850px] flex-col gap-4">
            <div className="font-simianregular text-[20px] leading-none text-[#0B0B0B]">
              Travel requirements
            </div>
            <div>
              <Input
                name="travelRequirements[chip]"
                label="Microchipping (ISO-standard microchip)"
                placeholder="15-digit ID"
                value={pet.travelRequirements?.chip || ""}
                onChange={(e) =>
                  setPet((prevState) => {
                    return {
                      ...prevState,
                      travelRequirements: {
                        ...prevState.travelRequirements,
                        chip: e.target.value,
                      },
                    };
                  })
                }
                error={errors.travelRequirements}
              />
            </div>
            <div>
              <Select
                name="travelRequirements"
                label="Health Certificate or Pet Passport"
                value={pet.travelRequirements?.passport || ""}
                placeholder="Select option"
                options={[
                  ["1", "Yes"],
                  ["0", "No"],
                ]}
                onChange={(e) =>
                  setPet((prevState) => {
                    return {
                      ...prevState,
                      travelRequirements: {
                        ...prevState.travelRequirements,
                        passport: e.target.value,
                      },
                    };
                  })
                }
                error={errors.travelRequirements}
              />
            </div>
          </div>
        </div>
      </SideBarContent>
      <SideBarFooter>{renderFooter()}</SideBarFooter>
    </>
  );
};

export default function UserPageDogEditWrapper() {
  return (
    <RequireUserAuth>
      <UserPageDogEdit />
    </RequireUserAuth>
  );
}
